// Operaciones · Ventas en vivo
// Monitor de anomalías de ventas (online + POS) en una ventana móvil, con
// auto-refresh. Semáforo: rojo = cobro sin inscripción / duplicados (crítico).

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Card, Table, Tag, Space, Typography, Row, Col, Statistic, Button, Select, Switch, Alert, Empty, Tooltip
} from 'antd';
import {
  ReloadOutlined, CheckCircleTwoTone, WarningOutlined, ExclamationCircleOutlined,
  DollarOutlined, FileExclamationOutlined, CopyOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import OpsV2 from '../../../../service/ops.service';
import { mensajeDeError } from '../../../../service/client';
import { useNotify } from '../../../../providers/NotificationProvider';

const { Title, Text, Paragraph } = Typography;

const SEV = {
  critico: { color: 'red', label: 'Crítico' },
  alto: { color: 'orange', label: 'Alto' },
  medio: { color: 'gold', label: 'Medio' },
};
const CAT = {
  sin_inscripcion: 'Cobro sin inscripción',
  sin_boleta: 'Boleta no emitida',
  monto: 'Monto / descuento',
  duplicado: 'Duplicado',
};

export default function VentasEnVivo() {
  const notify = useNotify();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lookback, setLookback] = useState(180);
  const [auto, setAuto] = useState(true);
  const timer = useRef(null);

  const cargar = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try {
      const d = await OpsV2.ventasEnVivo({ lookback });
      setData(d);
    } catch (err) {
      notify.error(mensajeDeError(err, 'No se pudo cargar el monitor'));
    } finally {
      setLoading(false);
    }
  }, [lookback, notify]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (auto) {
      timer.current = setInterval(() => cargar(true), 60_000);
      return () => clearInterval(timer.current);
    }
  }, [auto, cargar]);

  const r = data?.resumen || {};
  const critico = (r.sinInscripcion || 0) + (r.duplicados || 0);
  const sano = data && (data.anomalias?.length || 0) === 0;

  const columns = [
    { title: 'Sev.', dataIndex: 'severidad', width: 90, render: s => <Tag color={SEV[s]?.color}>{SEV[s]?.label || s}</Tag> },
    { title: 'Tipo', dataIndex: 'tipo', width: 80, render: t => <Tag color={t === 'online' ? 'blue' : 'purple'}>{t === 'online' ? 'Online' : 'POS'}</Tag> },
    { title: 'Categoría', dataIndex: 'categoria', width: 150, render: c => CAT[c] || c },
    { title: 'Código', dataIndex: 'codigo', width: 130, render: c => <Text code>{c || '—'}</Text> },
    { title: 'Cliente', dataIndex: 'cliente', ellipsis: true, render: c => c || <Text type="secondary">—</Text> },
    { title: 'Monto', dataIndex: 'monto', width: 110, align: 'right', render: m => m ? `$${m.toLocaleString('es-CL')}` : '—' },
    { title: 'Detalle', dataIndex: 'detalle', ellipsis: true, render: d => <Tooltip title={d}>{d}</Tooltip> },
    { title: 'Hace', dataIndex: 'minutos', width: 70, render: m => m != null ? `${m}m` : '—' },
    { title: 'Remediación', dataIndex: 'remediacion', width: 180, render: x => x ? <Text code style={{ fontSize: 11 }}>{x}</Text> : '—' },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
        <Paragraph type="secondary" style={{ margin: 0 }}>
          Anomalías de ventas de los últimos {lookback} min. {data && <Text type="secondary">Actualizado {dayjs(data.generadoEn).format('HH:mm:ss')}</Text>}
        </Paragraph>
        <Space wrap>
          <Select value={lookback} onChange={setLookback} style={{ width: 140 }} options={[
            { value: 60, label: 'Últimos 60 min' }, { value: 180, label: 'Últimos 180 min' },
            { value: 720, label: 'Últimas 12 h' }, { value: 1440, label: 'Últimas 24 h' },
          ]} />
          <Space size={4}><Switch checked={auto} onChange={setAuto} size="small" /><Text type="secondary" style={{ fontSize: 12 }}>Auto 1min</Text></Space>
          <Button type="primary" icon={<ReloadOutlined />} onClick={() => cargar()} loading={loading}>Refrescar</Button>
        </Space>
      </Space>

      {sano && (
        <Alert type="success" showIcon icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} style={{ marginBottom: 12 }}
          message="Sin anomalías en la ventana" description={`${r.pagosOnline || 0} pagos online + ${r.ventasPos || 0} ventas POS revisados. Todo cuadra.`} />
      )}
      {critico > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }}
          message={`${critico} caso(s) crítico(s): cobro sin inscripción o duplicados`} description="Revisar y remediar cuanto antes." />
      )}

      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Cobro sin inscripción" value={r.sinInscripcion ?? 0} valueStyle={{ color: r.sinInscripcion ? '#ff4d4f' : '#52c41a' }} prefix={<ExclamationCircleOutlined />} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Boleta no emitida" value={r.sinBoleta ?? 0} valueStyle={{ color: r.sinBoleta ? '#faad14' : '#52c41a' }} prefix={<FileExclamationOutlined />} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Monto / descuento" value={r.monto ?? 0} valueStyle={{ color: r.monto ? '#faad14' : '#52c41a' }} prefix={<DollarOutlined />} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Duplicados" value={r.duplicados ?? 0} valueStyle={{ color: r.duplicados ? '#ff4d4f' : '#52c41a' }} prefix={<CopyOutlined />} /></Card></Col>
      </Row>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey={(row) => `${row.categoria}-${row.id}-${row.codigo}`}
          columns={columns}
          dataSource={data?.anomalias || []}
          loading={loading}
          size="small"
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin anomalías" /> }}
          rowClassName={(row) => row.severidad === 'critico' ? 'ops-row-critico' : ''}
        />
      </Card>
    </div>
  );
}
